// eslint-disable-next-line no-use-before-define
import { useMutation, useQuery } from '@apollo/client';
import { ROInlinePatientSearch } from 'op-components';
import NavigationDropDown from 'op-components/NavigationDropDown/NavigationDropDown';
import { PermissionType } from 'op-enums';
import React, { useContext, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Notification } from 'shared-components/images';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import { canUseInlinePatientSearch, isViewer } from './HeaderBar';
import { HAS_LATEST_NOTIFICATION, TENANT_CONFIG_QUERY, UPDATE_USER_NOTIFICATION } from './HeaderBarQueries';
import { isDemo } from 'op-utils';
import OutageBanner from 'op-components/Banner/Outage';
import { CustomTheme, IconButton, Stack, useTheme, AppBar, Link as MUILink } from '@mui/material';
import { styled } from '@mui/system';
import { Search as SearchIcon } from '@mui/icons-material';
import { UserContext } from 'op-contexts';

interface Props {
  children: any;
  primaryRole: string;
  isPso: boolean;
  firstName: string;
  lastName: string;
  /** From USER_QUERY (HeaderBar); UserContext is empty outside ROBase (e.g. PSO /search). */
  permissions: string[];
  recentPatients?: any;
  styledProps?: boolean;
  toggleSearchModal?: () => void;
}

interface NavOptionType {
  label: string;
  linkTo: string;
  id: string;
  external?: boolean;
}

const NotificationIcon = styled((props: any) => <Notification title="notificationIcon.svg" {...props} />)`
  margin-top: -5px;
`;

const NotificationCircle = styled('div')`
  margin: -24px 0px 0px 130px;
`;

const StyledName = styled('div')`
  color: white;
  font-size: 16px;
  width: 150px;
  text-align: right;
`;

const InternalHeader = ({
  children,
  primaryRole,
  isPso,
  firstName,
  lastName,
  recentPatients,
  permissions,
}: Props): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const windowSize = useWindowSize();
  const isSmallDisplay = windowSize.width < 1440;
  const [isInlineSearchVisible, setIsInlineSearchVisible] = useState(false);
  const { data: notificationData, loading: notificationLoading } = useQuery(HAS_LATEST_NOTIFICATION);
  const showNotificationUpdate = notificationLoading
    ? false
    : !notificationData?.hasLatestNotification?.hasLatest && isPso;
  const [updateLatestNotification] = useMutation(UPDATE_USER_NOTIFICATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: HAS_LATEST_NOTIFICATION }],
  });
  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);
  const { hasFeature } = useContext(UserContext);
  const hasPermission = (permissionName: string) => permissions.includes(permissionName);
  const showLinkToLumonusPlanning = hasFeature('LinkToLumonusPlanning');
  const canAccessRadiation = hasPermission(PermissionType.RADIATION_ACCESS);
  const canViewCareplanDashboard = hasPermission(PermissionType.CAREPLAN_DASHBOARD_VIEW);
  const showDoctorPrefix = hasPermission(PermissionType.DOCTOR_PREFIX);
  const canAccessInsights =
    hasPermission(PermissionType.INSIGHTS_ACCESS) || hasPermission(PermissionType.INSIGHTS_SUPERUSER_ACCESS);
  const canViewLatestUpdates = hasPermission(PermissionType.LATEST_UPDATES_VIEW);
  const canAccessNurse = hasPermission(PermissionType.NURSE_ACCESS);

  const canAccessMedOnc = hasPermission(PermissionType.MO_ACCESS);
  const canAccessPso = hasPermission(PermissionType.PSO_ACCESS);
  const showInlinePatientSearch = canUseInlinePatientSearch(canAccessRadiation, canAccessMedOnc);
  const displayHomeLink = canAccessRadiation || canAccessMedOnc || canAccessPso || canAccessNurse;

  const getHomePath = (): string => {
    if (canAccessRadiation) {
      return '/radiation';
    }
    if (canAccessMedOnc) {
      return '/medonc';
    }
    if (canAccessPso) {
      return '/search';
    }
    if (canAccessInsights) {
      return '/radiation/insights';
    }
    return '/search';
  };

  const home = getHomePath();

  const redCircle = (
    <NotificationCircle>
      <svg className="notification-red-circle" height="15" width="15">
        <circle cx="7.5" cy="7.5" r="7.5" fill={theme.palette.error.main} />
        <text fontSize="0.8em" x="28%" y="50%" fill="white" dy=".35em">
          1
        </text>
      </svg>
    </NotificationCircle>
  );

  const ExternalHeaderLink = ({
    id,
    href,
    children,
    onClick,
  }: {
    id: string;
    href: string;
    children: React.ReactNode;
    onClick?: () => void;
  }) => (
    <MUILink
      id={id}
      component={'a'}
      href={href}
      rel={'noopener noreferrer'}
      underline="none"
      color="inherit"
      variant="subtitle2"
      onClick={onClick}
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        paddingX: 2,
        borderBottom: '2px solid transparent',
        paddingTop: '2px',
      }}>
      {children}
    </MUILink>
  );

  const navOptions: NavOptionType[] = [];
  if (displayHomeLink) {
    navOptions.push({
      label: canAccessRadiation ? 'APPOINTMENTS' : 'DASHBOARD',
      linkTo: home,
      id: 'appointments',
    });
  }

  if (canViewCareplanDashboard) {
    navOptions.push({
      label: 'CAREPLANS',
      linkTo: '/radiation/careplans',
      id: 'careplans',
    });
  }
  if (isDemo || showLinkToLumonusPlanning) {
    navOptions.push({
      label: 'PLANNING',
      linkTo: '/sso/lumonus_planning_redirect',
      external: true,
      id: 'planning',
    });
  }
  if ((tenantConfigData?.tenantConfig?.insightsVisible && canAccessInsights) || isDemo) {
    navOptions.push({
      label: 'INSIGHTS',
      linkTo: isDemo ? 'https://analytics.app.lumonus.health' : '/radiation/insights',
      id: 'insights',
      external: isDemo ? true : false,
    });
  }

  const toggleInlineSearchModal = (e: React.MouseEvent<HTMLElement>) => {
    setIsInlineSearchVisible((visible) => !visible);
    e.stopPropagation();
  };

  const showLatestUpdates = Boolean(tenantConfigData?.tenantConfig?.showLatestUpdates) && canViewLatestUpdates;
  const LatestUpdatesLink = () => {
    const linkTo = 'https://genesiscare.sharepoint.com/sites/Oncologyportal/SitePages/Latest-Release-information.aspx';
    return (
      <ExternalHeaderLink
        id="sharepoint-link"
        href={linkTo}
        onClick={() => {
          if (showNotificationUpdate) {
            updateLatestNotification();
          }
        }}>
        LATEST UPDATES
        <NotificationIcon />
        {showNotificationUpdate && redCircle}
      </ExternalHeaderLink>
    );
  };

  return (
    <>
      <OutageBanner />
      <AppBar id="internal-header" position="static" sx={{ zIndex: 999999, height: '56px' }}>
        <Stack
          minHeight={56}
          height={1}
          flex={1}
          direction="row"
          alignItems={'center'}
          justifyContent={'space-between'}
          paddingX={2}>
          <Stack height={1} alignItems="center" direction="row">
            <Link to={home} id="gc-logo-navigator" style={{ marginRight: '80px' }}>
              {theme?.custom?.logo}
            </Link>
            {navOptions.map(
              (option: NavOptionType): JSX.Element =>
                option.external ? (
                  <ExternalHeaderLink key={option.label} id={option.id} href={option.linkTo}>
                    {option.label}
                  </ExternalHeaderLink>
                ) : (
                  <MUILink
                    key={option.label}
                    id={option.id}
                    component={NavLink}
                    to={option.linkTo}
                    underline="none"
                    color="inherit"
                    variant="subtitle2"
                    exact={true}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      paddingX: 2,
                      borderBottom: '2px solid transparent', // To prevent the text shifting when active made active
                      paddingTop: '2px',
                      '&.active': {
                        borderBottom: '2px solid white',
                      },
                    }}>
                    {option.label}
                  </MUILink>
                ),
            )}
            {showLatestUpdates && <LatestUpdatesLink />}
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            {showInlinePatientSearch ? (
              <div role="presentation" className={`inline-search-div ${isInlineSearchVisible ? 'active-search' : ''}`}>
                {!isInlineSearchVisible && (
                  <IconButton onClick={toggleInlineSearchModal} data-testid="search-icon">
                    <SearchIcon sx={{ color: theme.palette.primary.contrastText }} />
                  </IconButton>
                )}
                {isInlineSearchVisible && (
                  <div style={{ color: 'initial' }}>
                    <ROInlinePatientSearch
                      primaryRole={primaryRole}
                      closeSearch={() => setIsInlineSearchVisible(false)}
                      recentPatients={recentPatients}
                    />
                  </div>
                )}
              </div>
            ) : (
              !isViewer(primaryRole) && (
                <StyledName id="logged-in-user-name">
                  {isSmallDisplay ? firstName[0] + '. ' + lastName : firstName + ' ' + lastName}
                </StyledName>
              )
            )}
            {
              // @ts-ignore
              <NavigationDropDown
                name={firstName + ' ' + lastName}
                forROPortal={showInlinePatientSearch}
                showDoctorPrefix={showDoctorPrefix}
              />
            }
          </Stack>
        </Stack>
      </AppBar>
      {children && React.cloneElement(children, { primaryRole })}
    </>
  );
};

export default InternalHeader;
